import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createApiClient } from '../lib/apiClient';
import { Camera, Loader2, Receipt, CheckCircle, Clock, Fuel, XCircle, Eye } from 'lucide-react';
import { FuelReceiptUploader, openReceipt } from './FuelReceiptUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = createApiClient();
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusPill = (s) => {
  if (s === 'Approved') return 'bg-green-50 text-green-700 border-green-200';
  if (s === 'Rejected') return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-amber-50 text-amber-700 border-amber-200';
};

const DriverReceiptCard = ({ activeLoadId = null, stopPrefill = null, refreshKey = 0 }) => {
  const [receipts, setReceipts] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, total: 0 });
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [stopType, setStopType] = useState(null);
  const [mode, setMode] = useState('fuel');
  const cardRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await ax.get('/tax/driver/my-receipts'); setReceipts(r.data.receipts || []); setCounts(r.data.counts || {}); } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshKey) { load(); } }, [refreshKey, load]);

  useEffect(() => {
    if (stopPrefill && stopPrefill.stopType) {
      if (stopPrefill.done) {
        setMsg('Stop + receipt saved together. Thanks!');
        setStopType(null); setNote('');
      } else {
        setStopType(stopPrefill.stopType);
        setNote(stopPrefill.note || '');
        setMsg('');
      }
      if (cardRef.current) cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [stopPrefill]);

  const submit = async (fileList) => {
    const file = fileList?.[0]; if (!file) return;
    setUploading(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (note.trim()) fd.append('note', note.trim());
      if (activeLoadId) fd.append('load_id', activeLoadId);
      if (stopType) fd.append('stop_type', stopType);
      const r = await ax.post('/tax/driver/receipt', fd);
      setNote(''); setStopType(null);
      setMsg(r.data?.unreadable
        ? "Sent, but we couldn't read the details — the office will fill them in."
        : 'Receipt sent to the office for review. Thanks!');
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div ref={cardRef} className={`bg-white border rounded-xl p-5 transition-all ${stopType ? 'border-[#002FA7] ring-2 ring-[#002FA7]/30' : 'border-slate-200'}`} data-testid="driver-receipt-card">
      {stopType && (
        <div data-testid="driver-receipt-stop-banner" className="mb-3 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-sm text-[#002FA7] font-semibold capitalize">
          {stopType} stop logged — add the receipt now.
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-[#002FA7]" />
          <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Submit a Receipt</h3>
        </div>
        {counts.total > 0 && (
          <span className="text-xs text-slate-400" data-testid="driver-receipt-counts">{counts.pending} pending · {counts.approved} approved</span>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        {mode === 'fuel' ? 'Snap your fuel receipt. We read the date, total, gallons and station — you confirm them before it goes to the office.' : 'Snap a photo of a toll, scale, lumper or repair receipt. Our system reads it and sends it to the office.'}
      </p>
      <div className="flex gap-1 mb-3" data-testid="driver-receipt-mode">
        <button type="button" data-testid="driver-receipt-mode-fuel" onClick={() => setMode('fuel')} className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full border ${mode === 'fuel' ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'border-slate-300 text-slate-700'}`}><Fuel className="w-3.5 h-3.5" /> Fuel receipt</button>
        <button type="button" data-testid="driver-receipt-mode-other" onClick={() => setMode('other')} className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full border ${mode === 'other' ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'border-slate-300 text-slate-700'}`}><Receipt className="w-3.5 h-3.5" /> Other receipt</button>
      </div>

      {mode === 'fuel' ? (
        <FuelReceiptUploader role="driver" loadId={activeLoadId || ''} onSaved={() => { setMsg('Fuel receipt submitted — pending office approval. It will not affect your pay until approved.'); load(); }} />
      ) : (<>
      <input
        type="text"
        data-testid="driver-receipt-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional note (e.g. 'Scale — CAT, I-40')"
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30"
      />

      <label
        data-testid="driver-receipt-upload"
        className={`flex items-center justify-center gap-2 bg-[#002FA7] text-white px-4 py-3 rounded-md text-sm font-semibold cursor-pointer hover:bg-[#00247D] transition-all ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        {uploading ? 'Uploading…' : 'Take / Choose Photo'}
        <input type="file" accept="image/*,.pdf" capture="environment" className="hidden" onChange={(e) => submit(e.target.files)} />
      </label>
      </>)}

      {msg && <p className="text-xs mt-2 text-[#002FA7]" data-testid="driver-receipt-msg">{msg}</p>}

      {receipts.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>My recent receipts</p>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {receipts.slice(0, 12).map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`driver-receipt-${r.id}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <div className="min-w-0">
                  <p className="text-slate-800 font-medium truncate">{r.expense_kind === 'fuel' ? 'Fuel · ' : ''}{r.vendor_name || r.note || 'Receipt'} {r.amount ? `· ${money(r.amount)}` : ''}{r.fuel_gallons ? ` · ${r.fuel_gallons} gal` : ''}</p>
                  <p className="text-[11px] text-slate-400">{r.expense_date}{r.paid_by ? ` · paid by ${r.paid_by}` : ''}{r.review_status === 'Approved' && r.expense_kind === 'fuel' ? (r.deduct_from_pay ? ' · deducted from pay' : ' · not deducted') : ''}{r.rejection_reason ? ` · ${r.rejection_reason}` : ''}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {r.expense_kind === 'fuel' && <button type="button" data-testid={`driver-receipt-view-${r.id}`} onClick={() => openReceipt(r.id)} title="View receipt" className="p-1 rounded hover:bg-slate-100 text-slate-500"><Eye className="w-3.5 h-3.5" /></button>}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${statusPill(r.review_status)}`}>
                    {r.review_status === 'Approved' ? <CheckCircle className="w-3 h-3" /> : r.review_status === 'Rejected' ? <XCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    {r.review_status === 'Approved' ? 'Approved' : r.review_status === 'Rejected' ? 'Rejected' : 'Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverReceiptCard;
